/**
 * Collect unique tickers
 */
import { useEffect, useRef, useState } from 'react';
import { ITicker } from '@kattana-io/common';
import { KattanaAPI } from '../../store/createStore';

type Item = {
  chain: string;
  pair: string;
  trades?: Array<{ chain: string; pair: string }>;
};

// filter items with empty pair field and get keys for the rest
const createDictionary = (data: Item[]): string[] =>
  data.reduce<string[]>((acc, item) => {
    if (item.pair) {
      const itemKey = `${item.chain}-${item.pair}`;
      if (!acc.includes(itemKey)) {
        acc.push(itemKey);
      }
    }
    if (item.trades?.length) {
      item.trades.forEach((subItem) => {
        if (subItem.pair) {
          const itemKey = `${subItem.chain}-${subItem.pair}`;
          if (!acc.includes(itemKey)) {
            acc.push(itemKey);
          }
        }
      });
    }
    return acc;
  }, []);

type tickersMap = Record<string, ITicker>;

const createMergeState = async (tickers: tickersMap, tickersKeys: string[]) => {
  const newState: tickersMap = {};
  for await (const key of tickersKeys) {
    if (!tickers[key]) {
      const [chain, ...address] = key.split('-');
      const pairAddress = address.join('-');

      const group = await KattanaAPI.tickers({ address: pairAddress, chain });
      if (Array.isArray(group) && group[0]) {
        const item = group[0];
        newState[key] = item;
      }
    }
  }
  return newState;
};

const useCollectTickers = (data: Item[]) => {
  const tickers = useRef<tickersMap>({});
  const [loadingTickers, setLoadingTickers] = useState<boolean>(false);

  // Collect tickers
  useEffect(() => {
    let flag = true;
    setLoadingTickers(true);

    if (data?.length) {
      const keys = createDictionary(data);
      createMergeState(tickers.current, keys).then((merge: tickersMap) => {
        if (flag) {
          tickers.current = {
            ...tickers.current,
            ...merge,
          };
          setLoadingTickers(false);
        }
      });
    } else {
      if (flag) {
        tickers.current = {};
        setLoadingTickers(false);
      }
    }

    return () => {
      flag = false;
    };
  }, [data]);

  return {
    tickers: tickers.current,
    loadingTickers,
  };
};

export default useCollectTickers;
